/**
 * Rhythm Intelligence — behavioral memory (persisted in user_settings.suggestion_memory).
 */

import { getDayOfWeek } from "@/lib/dateManager";
import { supabase } from "@/integrations/supabase/client";
import { storage } from "@/features/app-context/utils";

const MAX_EVENTS = 48;
const LEGACY_STORAGE_PREFIX = "or_suggestion_memory";

export interface SuggestionMemory {
  version: 2;
  dowAffinity: number[];
  spacingPreference: number;
  leadTimePreference: number;
  burstTolerance: number;
  totalAccepts: number;
  totalRejects: number;
  recentAccepts: MemoryEvent[];
  recentRejects: MemoryEvent[];
}

interface MemoryEvent {
  dates: string[];
  at: number;
}

export function emptyMemory(): SuggestionMemory {
  return {
    version: 2,
    dowAffinity: [0, 0, 0, 0, 0, 0, 0],
    spacingPreference: 2,
    leadTimePreference: 5,
    burstTolerance: 0.45,
    totalAccepts: 0,
    totalRejects: 0,
    recentAccepts: [],
    recentRejects: [],
  };
}

function migrateV1(raw: Record<string, unknown>): SuggestionMemory {
  const mem = emptyMemory();
  const acc = raw.dowAccepted as number[] | undefined;
  const rej = raw.dowRejected as number[] | undefined;
  if (Array.isArray(acc) && acc.length === 7) {
    mem.dowAffinity = acc.map((a, i) => {
      const r = Array.isArray(rej) ? (rej[i] ?? 0) : 0;
      return Math.max(-1, Math.min(1, (a - r) * 0.15));
    });
  }
  mem.totalAccepts = Array.isArray(raw.acceptedDates) ? raw.acceptedDates.length : 0;
  mem.totalRejects = Array.isArray(raw.rejectedDates) ? raw.rejectedDates.length : 0;
  return mem;
}

function normalizeAffinity(arr: unknown): number[] {
  if (!Array.isArray(arr) || arr.length !== 7) return [0, 0, 0, 0, 0, 0, 0];
  return arr.map((n) =>
    typeof n === "number" && Number.isFinite(n) ? Math.max(-1, Math.min(1, n)) : 0,
  );
}

function clamp(n: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, n));
}

function normalizeMemory(parsed: Partial<SuggestionMemory> & { version?: number }): SuggestionMemory {
  if (parsed.version !== 2) {
    return migrateV1(parsed as Record<string, unknown>);
  }
  return {
    version: 2,
    dowAffinity: normalizeAffinity(parsed.dowAffinity),
    spacingPreference: clamp(parsed.spacingPreference ?? 2, 1, 5),
    leadTimePreference: clamp(parsed.leadTimePreference ?? 5, 1, 21),
    burstTolerance: clamp(parsed.burstTolerance ?? 0.45, 0, 1),
    totalAccepts: parsed.totalAccepts ?? 0,
    totalRejects: parsed.totalRejects ?? 0,
    recentAccepts: Array.isArray(parsed.recentAccepts) ? parsed.recentAccepts.slice(-MAX_EVENTS) : [],
    recentRejects: Array.isArray(parsed.recentRejects) ? parsed.recentRejects.slice(-MAX_EVENTS) : [],
  };
}

/** Parse JSONB from user_settings; null → empty defaults. */
export function parseSuggestionMemory(raw: unknown): SuggestionMemory {
  if (!raw || typeof raw !== "object") return emptyMemory();
  try {
    return normalizeMemory(raw as Partial<SuggestionMemory>);
  } catch {
    return emptyMemory();
  }
}

export function serializeSuggestionMemoryForDb(
  memory: SuggestionMemory | null | undefined,
): SuggestionMemory | null {
  if (!memory) return null;
  if (memory.totalAccepts === 0 && memory.totalRejects === 0) return null;
  return memory;
}

/** Read v1/v2 localStorage memory (sync); used once at boot before DB backfill. */
export function readLegacyLocalSuggestionMemory(userId: string): SuggestionMemory | null {
  for (const suffix of [`_v2_${userId}`, `_v1_${userId}`]) {
    const raw = storage.get(`${LEGACY_STORAGE_PREFIX}${suffix}`);
    if (!raw) continue;
    try {
      return normalizeMemory(JSON.parse(raw) as Partial<SuggestionMemory>);
    } catch {
      continue;
    }
  }
  return null;
}

function clearLegacyLocalSuggestionMemory(userId: string): void {
  storage.remove(`${LEGACY_STORAGE_PREFIX}_v2_${userId}`);
  storage.remove(`${LEGACY_STORAGE_PREFIX}_v1_${userId}`);
}

/** Drop any locally cached Rhythm Intelligence memory (e.g. factory reset). */
export function clearLocalSuggestionMemory(userId: string): void {
  clearLegacyLocalSuggestionMemory(userId);
}

/** One-time: persist localStorage memory to user_settings, then clear local keys. */
export async function backfillLegacySuggestionMemoryToDb(
  userId: string,
  memory: SuggestionMemory,
): Promise<void> {
  const saved = await persistSuggestionMemory(userId, memory);
  if (saved) clearLegacyLocalSuggestionMemory(userId);
}

export async function persistSuggestionMemory(
  userId: string,
  memory: SuggestionMemory,
): Promise<boolean> {
  const payload = serializeSuggestionMemoryForDb(memory);
  const { error } = await supabase
    .from("user_settings")
    .upsert(
      { user_id: userId, suggestion_memory: payload },
      { onConflict: "user_id" },
    );
  if (error) {
    console.warn("[Rhythm Intelligence] suggestion_memory persist failed:", error.message);
    return false;
  }
  return true;
}

function daysBetween(a: string, b: string): number {
  const [ay, am, ad] = a.split("-").map(Number);
  const [by, bm, bd] = b.split("-").map(Number);
  return Math.round(
    Math.abs(new Date(by, bm - 1, bd).getTime() - new Date(ay, am - 1, ad).getTime()) / 86_400_000,
  );
}

function ema(current: number, next: number, alpha: number): number {
  return current * (1 - alpha) + next * alpha;
}

function analyzeBatch(dates: string[]): {
  spacing: number;
  maxConsecutive: number;
  dowCounts: number[];
} {
  const sorted = [...dates].sort();
  const gaps: number[] = [];
  let maxConsecutive = 1;
  let streak = 1;
  const dowCounts = [0, 0, 0, 0, 0, 0, 0];

  sorted.forEach((d) => {
    dowCounts[getDayOfWeek(d)] += 1;
  });

  for (let i = 1; i < sorted.length; i++) {
    const gap = daysBetween(sorted[i - 1], sorted[i]);
    if (gap === 1) {
      streak++;
      maxConsecutive = Math.max(maxConsecutive, streak);
    } else {
      streak = 1;
      if (gap > 0) gaps.push(gap);
    }
  }

  const spacing =
    gaps.length > 0 ? gaps.reduce((a, b) => a + b, 0) / gaps.length : 2;

  return { spacing, maxConsecutive, dowCounts };
}

/** Pure update — caller persists via persistSuggestionMemory. */
export function applyMemoryFeedback(
  memory: SuggestionMemory,
  dates: string[],
  todayStr: string,
  positive: boolean,
): SuggestionMemory {
  if (dates.length === 0) return memory;

  const next: SuggestionMemory = {
    ...memory,
    dowAffinity: [...memory.dowAffinity],
    recentAccepts: [...memory.recentAccepts],
    recentRejects: [...memory.recentRejects],
  };

  const { spacing, maxConsecutive, dowCounts } = analyzeBatch(dates);
  const alpha = 0.28;

  next.spacingPreference = ema(
    next.spacingPreference,
    positive ? spacing : next.spacingPreference + 0.35,
    alpha,
  );

  const burstSignal = maxConsecutive <= 1 ? 0.2 : maxConsecutive === 2 ? 0.55 : 0.85;
  next.burstTolerance = ema(
    next.burstTolerance,
    positive ? burstSignal : Math.max(0, burstSignal - 0.35),
    alpha,
  );

  const leadTimes = dates.map((d) => daysBetween(todayStr, d)).filter((n) => n > 0);
  if (leadTimes.length > 0) {
    const avgLead = leadTimes.reduce((a, b) => a + b, 0) / leadTimes.length;
    next.leadTimePreference = ema(next.leadTimePreference, avgLead, alpha);
  }

  const total = dowCounts.reduce((a, b) => a + b, 0) || 1;
  dowCounts.forEach((count, i) => {
    const signal = (count / total) * 2 - 1;
    next.dowAffinity[i] = ema(
      next.dowAffinity[i],
      positive ? signal : signal * -0.5,
      alpha * 0.85,
    );
  });

  if (positive) {
    next.totalAccepts += 1;
    next.recentAccepts = [
      ...next.recentAccepts,
      { dates: [...dates], at: Date.now() },
    ].slice(-MAX_EVENTS);
  } else {
    next.totalRejects += 1;
    next.recentRejects = [
      ...next.recentRejects,
      { dates: [...dates], at: Date.now() },
    ].slice(-MAX_EVENTS);
  }

  return next;
}

export interface MemoryScoringSignals {
  dowBoost: number[];
  spacingIdeal: number;
  leadTimeIdeal: number;
  burstTolerance: number;
  trust: number;
}

export function getMemoryScoringSignals(memory: SuggestionMemory): MemoryScoringSignals {
  const trust = Math.min(1, (memory.totalAccepts + memory.totalRejects * 0.5) / 12);
  return {
    dowBoost: memory.dowAffinity.map((a) => a * 12 * trust),
    spacingIdeal: memory.spacingPreference,
    leadTimeIdeal: memory.leadTimePreference,
    burstTolerance: memory.burstTolerance,
    trust,
  };
}
